import { useContext, useState, useEffect } from "react";
import UserContext from "../../hooks/UserContext";
import { addUserObjectRestriction, deleteUserObjectRestriction, getUserObjectRestrictionsByCustomer } from "../../scripts/userObjectRestriction";
import { getUsersByCustomerId } from "../../scripts/user";
import { hasVenuePermission } from "../permission/permissions";
import { permissionTypes } from "../permission/permissions";
import { getUserDisplayName } from "../settings/user/userAdministration/userAdministrationUtils";
import DropdownMultiSelect from "../UiComponents/DropdownMultiSelect";


const ObjectRestrictionSelector = ({ objectId, objectType=null, placeholder=false}) => {

    const userContext = useContext(UserContext);
    const [restrictions, setRestrictions] = useState([]);
    const [venueUsers, setVenueUsers] = useState([]);

    useEffect(() => {
        // Fetch restrictions based on userContext
        fetchRestrictions();
        fetchVenueUsers();
    }, [userContext]);
    
    const fetchRestrictions = async () => {
        const fetchedRestrictions = await getUserObjectRestrictionsByCustomer(userContext.customer.customerId);
        setRestrictions(fetchedRestrictions);
    }

    const fetchVenueUsers = async () => {
        const users = await getUsersByCustomerId(userContext.customer.customerId);
        setVenueUsers(users);
    }

    // Get currently restricted user IDs for this specific object
    const getRestrictedUserIds = () => {
        return restrictions
            .filter(r => r.objectId === objectId && r.objectType === objectType)
            .map(r => r.userId.toString());
    };

    // Get user IDs who have ACCESS (inverse of restricted) - for dropdown selection
    const getUsersWithAccess = () => {
        const restrictedIds = new Set(getRestrictedUserIds());
        return venueUsers
            .filter(user => user.userId !== userContext.user.userId) // Exclude current user
            .filter(user => !restrictedIds.has(user.userId.toString())) // Exclude restricted users
            .map(user => user.userId.toString());
    };

    // Handle dropdown selection change - inverse logic: selected = has access (not restricted)
    const handleSelectionChange = async (selectedUserIds) => {
        const currentRestricted = new Set(getRestrictedUserIds());
        const selectedUsers = new Set(selectedUserIds);
        
        // All users except current user
        const allEligibleUsers = venueUsers
            .filter(user => user.userId !== userContext.user.userId)
            .map(user => user.userId.toString());

        // Users who should now be restricted = all eligible users - selected users
        const shouldBeRestricted = new Set(
            allEligibleUsers.filter(userId => !selectedUsers.has(userId))
        );

        // Find users to add restrictions for (newly restricted)
        const toRestrict = [...shouldBeRestricted].filter(userId => !currentRestricted.has(userId));
        // Find users to remove restrictions for (newly granted access)
        const toUnrestrict = [...currentRestricted].filter(userId => !shouldBeRestricted.has(userId));

        // Add new restrictions
        for (const userId of toRestrict) {
            await createNewRestriction(userId);
        }

        // Remove old restrictions (grant access)
        for (const userId of toUnrestrict) {
            await removeRestriction(userId);
        }

        // Refresh restrictions after changes
        await fetchRestrictions();
    };

    const createNewRestriction = async (userId) => {

        
        try {
            const newObjectRestriction = {
                customerId: userContext.customer.customerId,
                userId,
                objectId,
                objectType,
                createdByUserId: userContext.user.userId,
                createdByUserName: userContext.user.firstName + ' ' + userContext.user.lastName
            };
            await addUserObjectRestriction(newObjectRestriction);
        } catch (error) {
            console.error('Failed to create restriction:', error);
        }
    };

    const removeRestriction = async (userId) => {
        try {
            const restriction = restrictions.find(r => 
                r.userId === userId && 
                r.objectId === objectId && 
                r.objectType === objectType
            );
            if (restriction?.restrictionId) {
                await deleteUserObjectRestriction(restriction.restrictionId);
            }
        } catch (error) {
            console.error('Failed to remove restriction:', error);
        }
    };

    // Prepare dropdown items with user display names (exclude current user)
    const dropdownItems = venueUsers
        .filter(user => user.userId !== userContext.user.userId)
        .map(user => ({
            key: user.userId.toString(),
            label: getUserDisplayName(user)
        }));

    return (<>
        {hasVenuePermission(userContext, permissionTypes.userAdministrationWrite) && (
            <DropdownMultiSelect
                items={dropdownItems}
                selectedKeys={getUsersWithAccess()}
                onChange={handleSelectionChange}
                placeholder={placeholder || "Select users with access..."}
                customDisplayText={(selectedCount, totalCount) => {
                    const restrictedCount = totalCount - selectedCount;
                    if (restrictedCount === 0) return "0 users restricted";
                    return `${restrictedCount} user${restrictedCount === 1 ? '' : 's'} restricted`;
                }}
                selectAllText="All access"
                clearAllText="Restrict all"
            />
        )}
    </>);
}

export default ObjectRestrictionSelector;